import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Card,
  Typography,
  Button,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Stack,
} from "@mui/material";
import { Bar } from "react-chartjs-2";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import AutoGraphOutlinedIcon from "@mui/icons-material/AutoGraphOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import jsPDF from "jspdf";
import "jspdf-autotable";
import api from "../../utils/api";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import OverviewSummaryCards from "./OverviewSummaryCards";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);

const monthLabels = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const monthColors = [
  "#FFDDC1", "#FFABAB", "#FFC3A0", "#D4A5A5", "#85C1E9", "#A3E4D7",
  "#F9E79F", "#AED6F1", "#D7BDE2", "#D5F5E3", "#D6DBDF", "#E8DAEF",
];

const getMostCommonTypeByMonthByYear = (data) => {
  const result = {};

  data.forEach((item) => {
    const date = new Date(item.created_at);
    const year = date.getFullYear();
    const month = date.getMonth();
    const type = item.type_name || "Unknown";

    if (!result[year]) {
      result[year] = new Array(12).fill(null).map(() => ({}));
    }

    result[year][month][type] = (result[year][month][type] || 0) + 1;
  });

  const formatted = {};
  Object.keys(result).forEach((year) => {
    formatted[year] = result[year].map((monthData) => {
      const entries = Object.entries(monthData);
      if (entries.length === 0) return "N/A";
      return entries.sort((a, b) => b[1] - a[1])[0][0];
    });
  });

  return formatted;
};

const processYearlyMonthlyData = (data, dateField) => {
  const monthlyData = {};
  data.forEach((item) => {
    const date = new Date(item[dateField]);
    if (isNaN(date)) return;

    const year = date.getFullYear();
    const month = date.getMonth();

    if (!monthlyData[year]) {
      monthlyData[year] = new Array(12).fill(0);
    }
    monthlyData[year][month]++;
  });
  return monthlyData;
};

// Average each month across all years instead of summing totals
const getAllYearsMonthlyAverage = (monthlyDataByYear) => {
  const years = Object.values(monthlyDataByYear);

  if (!years.length) return new Array(12).fill(0);

  const averages = new Array(12).fill(0);

  for (let monthIdx = 0; monthIdx < 12; monthIdx++) {
    const monthValues = years.map((yearData) => Number(yearData[monthIdx]) || 0);
    const sum = monthValues.reduce((acc, value) => acc + value, 0);
    averages[monthIdx] = Math.round(sum / years.length);
  }

  return averages;
};

const forecastNextMonthWorkOrders = (monthlyData) => {
  const currentMonth = new Date().getMonth();

  const prev3 = [
    Number(monthlyData[(currentMonth - 2 + 12) % 12]) || 0,
    Number(monthlyData[(currentMonth - 1 + 12) % 12]) || 0,
    Number(monthlyData[currentMonth]) || 0,
  ];

  if (!prev3.length) return 0;

  return Math.round(
    prev3.reduce((sum, val) => sum + val, 0) / prev3.length
  );
};

const OverviewWorkOrders = () => {
  const [filter, setFilter] = useState({ startDate: "", endDate: "" });
  const [loading, setLoading] = useState(true);
  const [workOrdersData, setWorkOrdersData] = useState([]);
  const [viewMode, setViewMode] = useState("all");

  const handleViewChange = (event, newView) => {
    if (newView !== null) setViewMode(newView);
  };

  useEffect(() => {
    fetchWorkOrders();
  }, []);

  const fetchWorkOrders = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/getWorkOrders`);
      setWorkOrdersData(res.data || []);
    } catch (error) {
      console.error("Error fetching work orders:", error);
    } finally {
      setLoading(false);
    }
  };

  const monthlyDataByYear = processYearlyMonthlyData(workOrdersData, "created_at");

  const selectedYearData =
    viewMode === "all"
      ? getAllYearsMonthlyAverage(monthlyDataByYear)
      : monthlyDataByYear[viewMode] || new Array(12).fill(0);

  const yearColors = ["#A3E4D7", "#85C1E9", "#FFDDC1", "#FFC3A0", "#D7BDE2", "#AED6F1"];
  const summaryChartData = {
    labels: monthLabels,
    datasets: Object.entries(monthlyDataByYear).map(([year, data], idx) => ({
      label: year,
      data,
      backgroundColor: yearColors[idx % yearColors.length],
      borderRadius: 8,
      maxBarThickness: 34,
    })),
  };

  const summaryChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
      },
    },
    scales: {
      x: {
        grid: { display: false },
      },
      y: {
        beginAtZero: true,
        ticks: { precision: 0 },
      },
    },
  };

  const mostCommonTypesByMonthByYear = getMostCommonTypeByMonthByYear(workOrdersData);
  const forecastCount = forecastNextMonthWorkOrders(selectedYearData);

  const exportReport = (format) => {
    const dateField = "created_at";
    const filteredData = workOrdersData.filter((item) => {
      const itemDate = new Date(item[dateField]);
      const start = filter.startDate ? new Date(filter.startDate) : null;
      const end = filter.endDate ? new Date(filter.endDate) : null;
      return (!start || itemDate >= start) && (!end || itemDate <= end);
    });

    if (filteredData.length === 0) {
      alert("No records found in the selected date range.");
      return;
    }

    const fileName = `report_work_orders_${new Date().toISOString()}`;

    if (format === "PDF") {
      const doc = new jsPDF({ orientation: "landscape" });
      doc.setFont("helvetica", "bold");
      doc.setFontSize(16);
      doc.text("Work Orders Report", 14, 20);
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, 28);

      const rawHeaders = Object.keys(filteredData[0] || {});
      const headers = rawHeaders.map((h) =>
        h.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())
      );
      const body = filteredData.map((item) => rawHeaders.map((key) => item[key] || ""));

      doc.autoTable({
        head: [headers],
        body,
        styles: { font: "courier", fontSize: 9, cellPadding: 3 },
        headStyles: { fillColor: [60, 60, 60], textColor: [255, 255, 255] },
        alternateRowStyles: { fillColor: [245, 245, 245] },
        startY: 35,
        margin: { left: 14, right: 14 },
        theme: "grid",
      });

      doc.save(`${fileName}.pdf`);
    } else if (format === "CSV") {
      const headers = Object.keys(filteredData[0]);
      const csvRows = [headers.join(",")];
      filteredData.forEach((item) => {
        const values = headers.map((header) =>
          `"${String(item[header] || "").replace(/"/g, '""')}"`
        );
        csvRows.push(values.join(","));
      });

      const blob = new Blob([csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", `${fileName}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleFilterChange = (e) => {
    setFilter({ ...filter, [e.target.name]: e.target.value });
  };

  const pageCardSx = {
    borderRadius: "18px",
    border: "1px solid rgba(47,99,173,0.08)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
    backgroundColor: "rgba(255,255,255,0.96)",
    overflow: "hidden",
  };

  const sectionCardSx = {
    p: 2.5,
    borderRadius: "16px",
    border: "1px solid rgba(47,99,173,0.08)",
    backgroundColor: "rgba(255,255,255,0.98)",
    boxShadow: "0 4px 14px rgba(0,0,0,0.04)",
    height: "100%",
  };

  const primaryButtonSx = {
    backgroundColor: "#2f63ad",
    color: "white",
    fontWeight: 700,
    borderRadius: "10px",
    textTransform: "none",
    px: 2.25,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#1f4f94",
      boxShadow: "none",
    },
  };

  const secondaryButtonSx = {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
    px: 2,
    borderColor: "rgba(47,99,173,0.18)",
    color: "#223142",
    "&:hover": {
      borderColor: "#2f63ad",
      color: "#2f63ad",
      backgroundColor: "rgba(47,99,173,0.04)",
    },
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "rgba(255,255,255,0.96)",
      borderRadius: "10px",
    },
  };

  return (
    <Box>
      <Card sx={pageCardSx}>
        <Box
          sx={{
            px: { xs: 2, md: 3 },
            pt: 3,
            pb: 2,
            borderBottom: "1px solid rgba(47,99,173,0.08)",
            backgroundColor: "rgba(47,99,173,0.02)",
          }}
        >
          <Typography variant="body2" sx={{ color: "#6b778c" }}>
            Review monthly trends, forecast next activity, and export report data.
          </Typography>
        </Box>

        <Box sx={{ p: { xs: 2, md: 3 }, backgroundColor: "rgba(47,99,173,0.015)" }}>
          <Card sx={{ ...sectionCardSx, mb: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <FilterAltOutlinedIcon sx={{ color: "#2f63ad" }} />
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 700, color: "#223142" }}
              >
                Filters & Export
              </Typography>
            </Box>

            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Start Date"
                  type="date"
                  name="startDate"
                  value={filter.startDate}
                  onChange={handleFilterChange}
                  InputLabelProps={{ shrink: true }}
                  sx={fieldSx}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="End Date"
                  type="date"
                  name="endDate"
                  value={filter.endDate}
                  onChange={handleFilterChange}
                  InputLabelProps={{ shrink: true }}
                  sx={fieldSx}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: { xs: "flex-start", md: "flex-end" },
                    gap: 1,
                    flexWrap: "wrap",
                  }}
                >
                  <Button
                    variant="outlined"
                    onClick={() => exportReport("CSV")}
                    startIcon={<FileDownloadIcon />}
                    sx={secondaryButtonSx}
                  >
                    Export CSV
                  </Button>

                  <Button
                    variant="contained"
                    onClick={() => exportReport("PDF")}
                    startIcon={<FileDownloadIcon />}
                    sx={primaryButtonSx}
                  >
                    Export PDF
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Card>

          <Box sx={{ mb: 3 }}>
            <OverviewSummaryCards
              stats={{
                total: workOrdersData.length,
                thisMonth: workOrdersData.filter(
                  (w) => new Date(w.created_at).getMonth() === new Date().getMonth()
                ).length,
                resolved: workOrdersData.filter(
                  (w) => w.status?.toLowerCase() === "resolved"
                ).length,
                pending: workOrdersData.filter(
                  (w) => w.status?.toLowerCase() !== "resolved"
                ).length,
                avgResolution: (() => {
                  const durations = workOrdersData
                    .filter((w) => w.resolved_at)
                    .map(
                      (w) =>
                        (new Date(w.resolved_at) - new Date(w.created_at)) /
                        (1000 * 60 * 60 * 24)
                    );
                  return durations.length
                    ? `${(
                        durations.reduce((a, b) => a + b, 0) / durations.length
                      ).toFixed(1)} days`
                    : "N/A";
                })(),
              }}
            />
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card sx={sectionCardSx}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                  <InsightsOutlinedIcon sx={{ color: "#2f63ad" }} />
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 700, color: "#223142" }}
                  >
                    Work Orders Trends (Monthly)
                  </Typography>
                </Box>

                <Box sx={{ height: 320 }}>
                  {loading ? (
                    <Box
                      sx={{
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <CircularProgress />
                    </Box>
                  ) : (
                    <Bar data={summaryChartData} options={summaryChartOptions} />
                  )}
                </Box>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card sx={sectionCardSx}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                  <AutoGraphOutlinedIcon sx={{ color: "#2f63ad" }} />
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 700, color: "#223142" }}
                  >
                    Forecast for Next Month {viewMode !== "all" ? `(${viewMode})` : ""}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    height: 320,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexDirection: "column",
                    gap: 1,
                  }}
                >
                  {loading ? (
                    <CircularProgress />
                  ) : (
                    <>
                      <Typography
                        variant="h2"
                        sx={{
                          fontWeight: 700,
                          color: "#2f63ad",
                          lineHeight: 1,
                        }}
                      >
                        {forecastCount}
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#6b778c" }}>
                        Estimated work orders next month
                      </Typography>
                    </>
                  )}
                </Box>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card sx={sectionCardSx}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: { xs: "flex-start", md: "center" },
                    flexDirection: { xs: "column", md: "row" },
                    gap: 2,
                    mb: 2,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CategoryOutlinedIcon sx={{ color: "#2f63ad" }} />
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 700, color: "#223142" }}
                    >
                      Most Common Work Order Type by Month
                    </Typography>
                  </Box>

                  <ToggleButtonGroup
                    value={viewMode}
                    exclusive
                    onChange={handleViewChange}
                    size="small"
                    color="primary"
                    sx={{
                      flexWrap: "wrap",
                      gap: 1,
                      "& .MuiToggleButton-root": {
                        textTransform: "none",
                        borderRadius: "10px !important",
                        border: "1px solid rgba(47,99,173,0.18)",
                        color: "#223142",
                        px: 1.75,
                      },
                      "& .Mui-selected": {
                        backgroundColor: "rgba(47,99,173,0.10) !important",
                        color: "#2f63ad !important",
                        fontWeight: 700,
                      },
                    }}
                  >
                    <ToggleButton value="all">All</ToggleButton>
                    {Object.keys(mostCommonTypesByMonthByYear).map((year) => (
                      <ToggleButton key={year} value={year}>
                        {year}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                </Box>

                <Grid container spacing={2}>
                  {monthLabels.map((month, idx) => (
                    <Grid item xs={12} sm={6} md={4} lg={2} key={idx}>
                      <Card
                        sx={{
                          p: 2,
                          minHeight: viewMode === "all" ? 132 : 96,
                          borderRadius: "14px",
                          backgroundColor: monthColors[idx % monthColors.length],
                          color: "#223142",
                          boxShadow: "0 4px 14px rgba(0,0,0,0.05)",
                          border: "1px solid rgba(255,255,255,0.35)",
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 700, mb: 1 }}
                        >
                          {month}
                        </Typography>

                        <Box sx={{ flex: 1 }}>
                          {viewMode === "all" ? (
                            <Stack spacing={0.5}>
                              {Object.entries(mostCommonTypesByMonthByYear).map(
                                ([year, values]) => (
                                  <Typography
                                    key={year}
                                    variant="body2"
                                    sx={{
                                      fontSize: 12,
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                    }}
                                  >
                                    {year}: <strong>{values[idx]}</strong>
                                  </Typography>
                                )
                              )}
                            </Stack>
                          ) : (
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {mostCommonTypesByMonthByYear[viewMode]?.[idx] || "N/A"}
                            </Typography>
                          )}
                        </Box>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </Card>
    </Box>
  );
};

export default OverviewWorkOrders;